<template>
  <span
    v-if="hasExpectedDeliveryDate"
    v-tooltip="{
      content: tooltipContent,
      theme: 'info-tooltip',
      popperClass: {
        'task__expected-delivery-tooltip': true,
        'is-item': isItem,
        'is-error': isExpectedDeliveryDateOverdue
      },
      placement: 'bottom'
    }"
    class="expected-delivery-indicator"
    v-bind="$attrs"
    :class="{ 'is-error': isExpectedDeliveryDateOverdue }"
    :style="{ color: iconColor }"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <icon
      data="@icon/calendar-full-in-row.svg"
      :fill="true"
      :width="iconSize"
      :height="iconSize"
    />
  </span>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import { useStore } from "vuex"

import { $t } from "@/plugins/i18n"
import { Getters } from "@/store"

defineOptions({
  name: "ExpectedDeliveryIndicator",
  inheritAttrs: false
})

const props = withDefaults(
  defineProps<{
    hasExpectedDeliveryDate?: boolean
    formattedExpectedDeliveryDate?: string
    isExpectedDeliveryDateOverdue?: boolean
    isItem?: boolean
    iconSize?: string
  }>(),
  {
    hasExpectedDeliveryDate: false,
    formattedExpectedDeliveryDate: "",
    isExpectedDeliveryDateOverdue: false,
    isItem: false,
    iconSize: "var(--icon-16)"
  }
)

const store = useStore()
const isHovered = ref(false)
const isDark = computed(() => store.getters[Getters.THEME_IS_DARK])

const tooltipContent = computed(() => {
  if (!props.isItem) {
    return `${$t("task.expectedDeliveryDate")}: <span class='task__expected-delivery-tooltip-date'>${props.formattedExpectedDeliveryDate}</span>`
  }

  const message = props.isExpectedDeliveryDateOverdue
    ? "One or more tasks has an overdue<br>Expected Delivery Date:"
    : "One or more tasks have an<br>Expected Delivery Date:"

  return `${message} <span class='task__expected-delivery-tooltip-date'>${props.formattedExpectedDeliveryDate}</span>`
})

const iconColor = computed(() => {
  if (props.isExpectedDeliveryDateOverdue) {
    if (isHovered.value) {
      return isDark.value ? "var(--serviceAlert-01)" : "var(--serviceAlert-03)"
    }

    return "var(--serviceAlert-02)"
  }

  if (isHovered.value) {
    return isDark.value ? "var(--monochrome-03)" : "var(--monochrome-06)"
  }

  return "var(--monochrome05to04)"
})

function onMouseEnter() {
  isHovered.value = true
}

function onMouseLeave() {
  isHovered.value = false
}
</script>

<style lang="scss" scoped>
.expected-delivery-indicator.task__delivery-date-indicator,
.expected-delivery-indicator.story__expected-delivery {
  svg path {
    fill: currentColor;
  }
}
</style>
